import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { blogPosts } from '../../data/blogs';
import SEO from '../SEO';
import Contact from '../Contact'; // Import Contact component

export default function Blog() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [posts, setPosts] = useState(blogPosts);
  const [activeCategory, setActiveCategory] = useState(searchParams.get('category') || 'all');
  const [activeTag, setActiveTag] = useState(searchParams.get('tag') || '');
  const [currentPage, setCurrentPage] = useState(1);
  const postsPerPage = 9;
  
  // Extract all unique categories
  const allCategories = ['all', ...new Set(blogPosts.flatMap(post => post.categories))];
  
  // Extract all unique tags
  const allTags = [...new Set(blogPosts.flatMap(post => post.tags))];
  
  // Filter posts based on active category and tag
  useEffect(() => {
    let filtered = [...blogPosts];
    
    if (activeCategory !== 'all') {
      filtered = filtered.filter(post => 
        post.categories.includes(activeCategory)
      );
    }
    
    if (activeTag) {
      filtered = filtered.filter(post => 
        post.tags.includes(activeTag)
      );
    }
    
    setPosts(filtered);
    setCurrentPage(1);
  }, [activeCategory, activeTag]);
  
  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (activeCategory !== 'all') {
      params.set('category', activeCategory);
    }
    if (activeTag) {
      params.set('tag', activeTag);
    }
    setSearchParams(params);
  }, [activeCategory, activeTag, setSearchParams]);
  
  // Get current posts for pagination
  const indexOfLastPost = currentPage * postsPerPage;
  const indexOfFirstPost = indexOfLastPost - postsPerPage;
  const currentPosts = posts.slice(indexOfFirstPost, indexOfLastPost);
  const totalPages = Math.ceil(posts.length / postsPerPage);
  
  // Handle category filter change
  const handleCategoryChange = (category) => {
    setActiveCategory(category);
    setActiveTag('');
  };
  
  // Handle tag filter change
  const handleTagChange = (tag) => {
    setActiveTag(tag === activeTag ? '' : tag);
  };
  
  // Schema for blog list page
  const blogListSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "BetterByAI Blog",
    "description": "AI insights, strategies, and best practices for businesses.",
    "url": "https://betterbyai.com/blog",
    "mainEntity": {
      "@type": "ItemList",
      "itemListElement": blogPosts.slice(0, 10).map((post, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "url": `https://betterbyai.com/blog/${post.slug}`,
        "name": post.title
      }))
    }
  };

  const blogOrgSchema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "BetterByAI Knowledge Center",
    "description": "Expert insights on AI implementation, automation strategies, and business transformation.",
    "url": "https://betterbyai.com/blog",
    "publisher": {
      "@type": "Organization",
      "name": "BetterByAI",
      "logo": {
        "@type": "ImageObject",
        "url": "https://betterbyai.com/logo.png",
        "width": "180",
        "height": "60"
      }
    },
    "blogPost": blogPosts.slice(0, 5).map(post => ({
      "@type": "BlogPosting",
      "headline": post.title,
      "description": post.excerpt,
      "datePublished": post.date,
      "author": {
        "@type": "Person",
        "name": post.author.name
      },
      "image": post.coverImage.src,
      "url": `https://betterbyai.com/blog/${post.slug}`,
      "keywords": post.tags.join(", ")
    }))
  };

  return (
    <div className="bg-white pt-24">
      <SEO 
        title="AI Transformation Insights | Strategic Implementation Guides | BetterByAI"
        description="Discover expert insights on AI implementation, automation strategies & ROI maximization. Actionable guides to transform your business with artificial intelligence."
        keywords="AI business blog, artificial intelligence implementation, automation strategies, generative AI insights, business transformation, AI ROI, machine learning tutorials, AI case studies, AI best practices, digital transformation"
        canonical="https://betterbyai.com/blog"
        schemaMarkup={[blogListSchema, blogOrgSchema]}
        speakable={true}
      />
      
      {/* Hero Section - simplified with no background */}
      <section className="relative pt-12 pb-16 overflow-hidden">
        {/* Removed background gradient and orbs */}
        
        <div className="container px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary mb-4"
            >
              BetterByAI Blog
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="text-xl text-secondary mb-8"
            >
              Insights, strategies, and best practices for transforming your business with AI.
            </motion.p>
          </div>
        </div>
      </section>
      
      {/* Filter Bar */}
      <section className="pb-8 border-b">
        <div className="container px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
            {/* Categories */}
            <div className="flex flex-wrap gap-2">
              {allCategories.map(category => (
                <button
                  key={category}
                  onClick={() => handleCategoryChange(category)}
                  className={`px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                    activeCategory === category
                      ? 'bg-[#FF7066] text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {category === 'all' ? 'All Posts' : category}
                </button>
              ))}
            </div>
            
            {/* Search Box */}
            <div className="relative w-full md:w-auto">
              <input
                type="text"
                placeholder="Search articles..."
                className="w-full md:w-64 border border-gray-300 rounded-full py-2 px-4 pl-10 focus:outline-none focus:ring-2 focus:ring-[#FF7066]/50 focus:border-transparent"
              />
              <svg
                className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                ></path>
              </svg>
            </div>
          </div>
        </div>
      </section>
      
      {/* Active Filters */}
      {activeTag && (
        <section className="py-4">
          <div className="container px-4 sm:px-6 lg:px-8">
            <div className="flex items-center">
              <span className="text-sm text-gray-500 mr-2">Active filter:</span>
              <button
                onClick={() => setActiveTag('')}
                className="flex items-center px-3 py-1 bg-[#FF7066]/10 text-[#FF7066] rounded-full text-sm font-medium"
              >
                #{activeTag}
                <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
          </div>
        </section>
      )}
      
      {/* Blog Posts Grid */}
      <section className="py-12">
        <div className="container px-4 sm:px-6 lg:px-8">
          {posts.length > 0 ? (
            <>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {currentPosts.map((post, index) => (
                  <motion.div
                    key={post.slug}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <Link
                      to={`/blog/${post.slug}`}
                      className="group block bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300"
                    >
                      <div className="h-56 overflow-hidden">
                        <img
                          src={post.coverImage.src}
                          alt={post.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                      <div className="p-6">
                        {/* Categories */}
                        <div className="flex flex-wrap gap-2 mb-3">
                          {post.categories.slice(0, 2).map(category => (
                            <span
                              key={category}
                              className="px-2 py-1 bg-[#FF7066]/10 text-[#FF7066] rounded-full text-xs font-medium"
                            >
                              {category}
                            </span>
                          ))}
                        </div>
                        
                        <h2 className="text-xl font-bold mb-2 text-primary group-hover:text-[#FF7066] transition-colors line-clamp-2">
                          {post.title}
                        </h2>
                        
                        <p className="text-gray-600 mb-4 line-clamp-3">{post.excerpt}</p>
                        
                        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
                          {/* Author - Simplified without image */}
                          <span className="text-sm text-gray-600">{post.author.name}</span>
                          
                          {/* Date */}
                          <span className="text-xs text-gray-500">
                            {format(new Date(post.date), 'MMM dd, yyyy')}
                          </span>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-12 flex justify-center">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className={`px-4 py-2 rounded-md ${
                        currentPage === 1
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Previous
                    </button>
                    
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`px-4 py-2 rounded-md ${
                          currentPage === page
                            ? 'bg-[#FF7066] text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                    
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className={`px-4 py-2 rounded-md ${
                        currentPage === totalPages
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="py-12 text-center">
              <h3 className="text-2xl font-bold text-gray-700 mb-4">No posts found</h3>
              <p className="text-gray-600 mb-6">There are no posts matching your selected filters.</p>
              <button
                onClick={() => {
                  setActiveCategory('all');
                  setActiveTag('');
                }}
                className="px-6 py-3 bg-[#FF7066] text-white rounded-lg hover:bg-[#FF7066]/90 transition-colors"
              >
                Reset Filters
              </button>
            </div>
          )}
        </div>
      </section>
      
      {/* Popular Tags */}
      <section className="py-12 bg-gray-50">
        <div className="container px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-primary mb-8">Popular Topics</h2>
          <div className="flex flex-wrap gap-3">
            {allTags.map(tag => (
              <button
                key={tag}
                onClick={() => handleTagChange(tag)}
                className={`px-4 py-2 text-sm font-medium rounded-full transition-colors ${
                  activeTag === tag
                    ? 'bg-[#FF7066] text-white'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                }`}
              >
                #{tag}
              </button>
            ))}
          </div>
        </div>
      </section>
      
      {/* Replace Newsletter section with Contact component */}
      <Contact />
    </div>
  );
}